import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';

import {useFaqBlock} from './useFaqBlock';

interface FaqItem {
    answer: string;
    id?: string;
    question: string;
}

/**
 * Props for the `FaqBlock` component.
 */
interface ComponentProps {
    /** Array of FAQ entries with question and answer. */
    items: FaqItem[];
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'FaqBlock'
     */
    testId?: string;
    /** Optional headline above the FAQ accordion. */
    title?: string | null;
}

/**
 * Renders an FAQ accordion block with expandable question/answer items.
 *
 * @param props        The component props.
 * @param props.items  Array of FAQ entries to display.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'FaqBlock'`.
 * @param props.title  Optional headline above the accordion.
 * @returns A JSX element rendering the FAQ accordion.
 *
 * @example
 * ```tsx
 * <FaqBlock items={[{question: 'Why?', answer: 'Because.'}]} title="FAQ" />
 * ```
 */
const FaqBlock = ({items, testId = 'FaqBlock', title}: ComponentProps) => {
    const {openId, toggle} = useFaqBlock();

    if (items.length === 0) return null;

    return (
        <Wrapper data-cy={testId}>
            {title && <Title>{title}</Title>}
            <AccordionList>
                {items.map(item => {
                    const id = item.id ?? item.question;
                    const isOpen = openId === id;

                    return (
                        <AccordionItem key={id} $isOpen={isOpen}>
                            <AccordionButton
                                aria-expanded={isOpen}
                                data-cy={`${testId}-item`}
                                type="button"
                                onClick={() => toggle(id)}
                            >
                                <Question>{item.question}</Question>
                                <IconWrapper $isOpen={isOpen}>
                                    <ChevronIcon />
                                </IconWrapper>
                            </AccordionButton>
                            <AnswerPanel $isOpen={isOpen} aria-hidden={!isOpen}>
                                <Answer>{item.answer}</Answer>
                            </AnswerPanel>
                        </AccordionItem>
                    );
                })}
            </AccordionList>
        </Wrapper>
    );
};

// ── Icons ───────────────────────────────────────────────────

/**
 * Simple chevron-down SVG icon for the accordion toggle.
 *
 * @returns An SVG chevron icon.
 */
const ChevronIcon = () => (
    <svg
        fill="none"
        height="20"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width="20"
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

FaqBlock.displayName = 'FaqBlock';

export {FaqBlock};

// ── Styled ──────────────────────────────────────────────────

const Wrapper = styled.section`
    display: flex;
    flex-direction: column;
    gap: ${spacing(4)};
`;

const Title = styled.h2`
    color: ${({theme}) => theme.colors.inkPrimary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 2.2rem;
    font-weight: 700;
    margin: 0;
`;

const AccordionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(2)};
`;

interface AccordionItemProps {
    $isOpen: boolean;
}

const AccordionItem = styled.div<AccordionItemProps>`
    background: ${({theme}) => theme.colors.surfaceLow};
    border-radius: 1.2rem;
    overflow: hidden;
    transition: background 0.2s ease;

    &:hover {
        background: ${({theme}) => theme.colors.surfaceMid};
    }
`;

const AccordionButton = styled.button`
    align-items: center;
    appearance: none;
    background: none;
    border: none;
    color: ${({theme}) => theme.colors.inkPrimary};
    cursor: pointer;
    display: flex;
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.6rem;
    font-weight: 600;
    gap: ${spacing(3)};
    justify-content: space-between;
    padding: ${spacing(5)} ${spacing(5)};
    text-align: left;
    width: 100%;
`;

const Question = styled.span`
    flex: 1;
`;

interface IconWrapperProps {
    $isOpen: boolean;
}

const IconWrapper = styled.span<IconWrapperProps>`
    color: ${({theme}) => theme.colors.inkTertiary};
    display: flex;
    flex-shrink: 0;
    transform: ${({$isOpen}) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 0.25s ease;
`;

interface AnswerPanelProps {
    $isOpen: boolean;
}

const AnswerPanel = styled.div<AnswerPanelProps>`
    max-height: ${({$isOpen}) => ($isOpen ? '50rem' : '0')};
    opacity: ${({$isOpen}) => ($isOpen ? 1 : 0)};
    overflow: hidden;
    transition: max-height 0.3s ease, opacity 0.25s ease;
`;

const Answer = styled.p`
    color: ${({theme}) => theme.colors.inkSecondary};
    font-family: ${({theme}) => theme.fonts.PlusJakartaSans};
    font-size: 1.5rem;
    line-height: 1.7;
    margin: 0;
    padding: 0 ${spacing(5)} ${spacing(5)};
    white-space: pre-line;
`;